import { IS_DEV, IS_TESTING } from "@/__main__/constants.mjs";
import type { CardBrand } from "@/feature-wallet/models/card-brand.mjs";
import type { PaymentPlan } from "@/feature-wallet/models/payment-plans.mjs";
import type { UserPaymentProvider } from "@/feature-wallet/models/user-payment-methods.mjs";
import type { UserSubscription } from "@/feature-wallet/models/user-subscriptions.mjs";
import type { UserTransaction } from "@/feature-wallet/models/user-transactions.mjs";
import Amex from "@/inline-assets/amex.svg";
import CB from "@/inline-assets/cb.svg";
import Diners from "@/inline-assets/dinners-club.svg";
import Discover from "@/inline-assets/discover-icon.svg";
import UnknownCard from "@/inline-assets/empty-card-icon.svg";
import Interac from "@/inline-assets/interac.svg";
import JCB from "@/inline-assets/jcb.svg";
import Mastercard from "@/inline-assets/mastercard-icon.svg";
import PayPal from "@/inline-assets/paypal-logo.svg";
import UnionPay from "@/inline-assets/union-pay.svg";
import Visa from "@/inline-assets/visa-icon.svg";

export const STRIPE_PUBLISHABLE_KEY =
  IS_DEV || IS_TESTING
    ? "pk_test_UI8rabJ3IfSzEQ0AzZ9wqiGE00kjiioSKt"
    : "pk_live_SCJlIlSRmhtRMgi8XZ7hhfFV00dxHDr8Wf";

interface PaymentTypeInfo {
  localization: [string, string];
  Icon: SVGComponent;
}

interface CardBrandInfo extends PaymentTypeInfo {
  providerId: string | null;
}

export const PAYPAL_INFO: PaymentTypeInfo = {
  localization: ["common:wallet.paymentType.paypal", "PayPal"],
  Icon: PayPal,
};

export const CARD_BRAND_INFO: Record<CardBrand, CardBrandInfo> = {
  amex: {
    providerId: "amex",
    localization: ["common:wallet.paymentType.amex", "American Express"],
    Icon: Amex,
  },
  diners: {
    providerId: "diners",
    localization: ["common:wallet.paymentType.diners", "Diners Club"],
    Icon: Diners,
  },
  discover: {
    providerId: "discover",
    localization: ["common:wallet.paymentType.discover", "Discover"],
    Icon: Discover,
  },
  jcb: {
    providerId: "jcb",
    localization: ["common:wallet.paymentType.jcb", "Japan Credit Bureau"],
    Icon: JCB,
  },
  mastercard: {
    providerId: "mastercard",
    localization: ["common:wallet.paymentType.mastercard", "Mastercard"],
    Icon: Mastercard,
  },
  unionpay: {
    providerId: "unionpay",
    localization: ["common:wallet.paymentType.unionpay", "UnionPay"],
    Icon: UnionPay,
  },
  visa: {
    providerId: "visa",
    localization: ["common:wallet.paymentType.visa", "Visa"],
    Icon: Visa,
  },

  // Will be marked as `unknown` from stripe
  cb: {
    providerId: null,
    localization: ["common:wallet.paymentType.cb", "Cartes Bancaires"],
    Icon: CB,
  },
  interac: {
    providerId: null,
    localization: ["common:wallet.paymentType.interac", "Interac"],
    Icon: Interac,
  },

  unknown: {
    providerId: "unknown",
    localization: ["common:wallet.paymentType.unknown", "Unknown"],
    Icon: UnknownCard,
  },
};

declare module "@/__main__/app-state.mjs" {
  interface FeatureState {
    wallet: {
      transactions: UserTransaction[];
      paymentProviders: UserPaymentProvider[];
      subscriptions: UserSubscription[];
      paymentPlans: { [planId: string]: PaymentPlan };
    };
  }

  interface VolatileState {
    proShowSuccess?: boolean;
    braintreeClientToken?: string;
  }
}
